import React from 'react';
import { useTranslation } from 'react-i18next';
import { languages } from '../i18n';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Globe, Check } from 'lucide-react';

export const LanguageSelector = ({ variant = 'compact' }) => {
  const { i18n, t } = useTranslation(['settings', 'common']);
  
  // Get current language (extract base code like 'en' from 'en-US')
  const currentLangCode = i18n.language?.split('-')[0]?.split('_')[0] || 'es';
  const currentLanguage = languages.find(lang => lang.code === currentLangCode) || languages[0];

  const handleLanguageChange = (languageCode) => {
    // Only save clean language codes (es, en) not regional variants
    i18n.changeLanguage(languageCode);
    localStorage.setItem('language', languageCode);
  };

  if (variant === 'compact') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4" />
                  <span className="sr-only">{t('common:languageSelector.title', 'Language')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className="flex items-center justify-between cursor-pointer"
                    data-testid={`language-${lang.code}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.nativeName}</span>
                    </span>
                    {currentLangCode === lang.code && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {t('common:languageSelector.title', 'Language')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Expanded variant for settings page
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-foreground">{t('common:languageSelector.title', 'Language')}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('common:languageSelector.description', 'Select the interface language')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
              currentLangCode === lang.code
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border hover:border-primary/50 hover:bg-muted text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`language-expanded-${lang.code}`}
          >
            <span className="text-2xl">{lang.flag}</span>
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium">{lang.nativeName}</span>
              <span className="text-xs opacity-70">{lang.name}</span>
            </div>
            {currentLangCode === lang.code && (
              <Check className="h-4 w-4 ml-auto text-primary" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSelector;
